// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Project } from '@/project/api/types';
import { ProjectReportDatum, ProjectReportMetric } from '@/report/types';
import {
  OTHER_PROJECT_DATA_ID,
  getProjectChartData,
  sumByCurrency,
} from '@/report/utilities/get-project-chart-data';
import { colors } from '@mui/material';
import { scaleLinear } from '@visx/scale';
import { sumBy } from 'lodash-es';

type ProjectDatum = Pick<
  Project,
  'id' | 'name' | 'assignments' | 'totalContractValue'
>;

interface Options {
  count: number;
  metric: ProjectReportMetric;
  colors: [string, string];
}

export function useProjectReportChart(
  projects: ProjectDatum[],
  options: Options
) {
  const data = getProjectChartData(projects, {
    count: options.count,
    metric: options.metric,
  });

  const colorScale = scaleLinear({
    domain: [0, options.count],
    range: options.colors,
  });

  const total = sumBy(data, (datum) => datum[options.metric]);
  const contractValueTotals = sumByCurrency(
    data.flatMap((datum) => datum.totalContractValueByCurrency)
  );

  // A project is "empty" for a given metric if it has no data, which is
  // distinct from having a value of 0.
  //
  // For example, a project with no assignments (i.e. no product configurations)
  // is considered empty while a project with assignments that all have a
  // requested count of zero is not considered empty.
  const isEmpty = projects.every((project) => {
    switch (options.metric) {
      case 'requestedCount':
        return project.assignments.length === 0;

      case 'totalContractValue':
        return project.totalContractValue === null;

      default:
        return options.metric satisfies never;
    }
  });

  const getColor = (id: string, index: number) => {
    if (id === OTHER_PROJECT_DATA_ID) {
      return colors.blueGrey[100];
    }

    return colorScale(index);
  };

  const getValue = (datum: ProjectReportDatum) => {
    return datum[options.metric];
  };

  return { data, isEmpty, total, contractValueTotals, getColor, getValue };
}
